import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';

import {
  Invitation,
  InvitationInstallationDetails,
  InvitationOwnerDetails,
  InvitationStatusType,
  InvitationTokenType,
} from '@vi-iot/client';

import { addDays } from '@common';
import { GTMService } from '@services';
import { isSendByBuildingManager } from '@store';

import { FormatPhoneNumberPipe } from '@pipes';
import { ViCardComponent, ViItemComponent } from '@vi-iot/ui/index';
import { CommonStandaloneModule } from 'src/app/shared/common-standalone.module';
import { AcceptInvitationDialogComponent } from '../accept-invitation-dialog/accept-invitation-dialog.component';
import { ViConfirmDirective } from './../../../../projects/ui/directives/confirm/confirm.directive';
import { RemainingPipe } from './../../pipes/remaining.pipe';

@Component({
  selector: 'app-invitation',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    ViCardComponent,
    ViItemComponent,
    ViConfirmDirective,
    FormatPhoneNumberPipe,
    RemainingPipe,
  ],
  templateUrl: './invitation.component.html',
  styleUrls: ['./invitation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvitationComponent {
  @Input() demo: boolean;
  @Input() invitation: Invitation;
  @Input() owner: InvitationOwnerDetails;
  @Input() installation: InvitationInstallationDetails;
  @Input() companyId: string;
  @Output() accept: EventEmitter<{ token: string; ownerName: string }> = new EventEmitter<{
    token: string;
    ownerName: string;
  }>();
  @Output() reject: EventEmitter<{ token: string; ownerName: string }> = new EventEmitter<{
    token: string;
    ownerName: string;
  }>();
  @Output() cancel: EventEmitter<{ token: string }> = new EventEmitter<{
    token: string;
  }>();

  get address(): string {
    const street = this.installation?.address?.street || '';
    const houseNumber = this.installation?.address?.houseNumber || '';
    const zip = this.installation?.address?.zip || '';
    const city = this.installation?.address?.city || '';

    return `${street} Str. ${houseNumber}, ${zip} ${city}`;
  }

  get gatewaySerial(): string {
    if (this.installation?.gateways?.length) {
      return this.installation.gateways[0].serial;
    }

    return '';
  }

  get date(): string {
    return this.invitation?.createdAt
      ? this.datePipe.transform(moment(this.invitation.createdAt).toDate(), 'short')
      : '';
  }

  get status(): string {
    switch (this.invitation.status) {
      case InvitationStatusType.Pending:
        switch (this.invitation.tokenType) {
          case InvitationTokenType.Invited:
            return isSendByBuildingManager(this.invitation, this.companyId)
              ? '+INVITATIONS.INVITATIONS.PENDING'
              : '+INVITATIONS.INVITATIONS.INCOMING';
          case InvitationTokenType.Requested:
            return '+INVITATIONS.INVITATIONS.PENDING';
        }
        break;
      case InvitationStatusType.Expired:
        return '+INVITATIONS.INVITATIONS.EXPIRED';
      case InvitationStatusType.Rejected:
        return '+INVITATIONS.INVITATIONS.DECLINED';
      default:
        return '';
    }
  }

  get isIncoming(): boolean {
    return (
      this.invitation.status === InvitationStatusType.Pending &&
      this.invitation.tokenType === InvitationTokenType.Invited &&
      !isSendByBuildingManager(this.invitation, this.companyId)
    );
  }

  get isCreated(): boolean {
    return (
      (this.invitation.status === InvitationStatusType.Pending &&
        this.invitation.tokenType === InvitationTokenType.Requested) ||
      (this.invitation.tokenType === InvitationTokenType.Invited &&
        isSendByBuildingManager(this.invitation, this.companyId))
    );
  }

  constructor(
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private gtmService: GTMService
  ) {}

  onReject(): void {
    this.reject.emit({
      token: this.invitation.token,
      ownerName: this.owner ? `${this.owner.firstName} ${this.owner.lastName}` : '',
    });
    this.gtmService.track('onConfirm', 'Reject invitation', 'invitation');
  }

  onDismissRejectInvitationDialog(): void {
    this.gtmService.track('onCancel', 'Reject invitation confirmation', 'invitation');
  }

  onCancel(): void {
    this.cancel.emit({
      token: this.invitation.token,
    });
    this.gtmService.track('onConfirm', 'Cancel invitation', 'invitation');
  }

  onDismissCancelInvitationDialog(): void {
    this.gtmService.track('onCancel', 'Cancel invitation confirmation', 'invitation');
  }

  openConfirmationDialog(): void {
    this.dialog
      .open(AcceptInvitationDialogComponent, {
        width: '520px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe((action) => {
        if (action && action.confirm) {
          this.accept.emit({
            token: this.invitation.token,
            ownerName: this.owner ? `${this.owner.firstName} ${this.owner.lastName}` : '',
          });
        }
      });
  }

  expirationDate(tokenExpirationDate: string): string {
    if (this.demo) {
      return addDays(new Date(), 3).toISOString();
    }

    return tokenExpirationDate;
  }
}
